// shell.ts ↔ game/Shell.cpp — ShellSession, the SHELL-time session substrate
// (the game.cpp:1276-1392 display partition + event-loop the C++ builds once at
// Game::Init and the shell forms run over; the GameSession builds the same
// substrate for the in-game slice) + the shell-form ids/controls/URLs and the
// ShellFlowDeps menu→host launch seam.
//
// The canvas main-menu state machine (Shell + MainMenuForm + RegisterNowForm)
// was removed (canvas-removal R1); the DOM menu drives launches now
// (dom/menu-controller.ts) via GameHost, which speaks the same ShellFlowDeps
// boundary. ShellSession still owns the prefs/options/sndm + the form managers
// that back the game's render surface.

import { IniReader } from '../../../sim/src/data/ini.ts';
import { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { CoordMap } from '../gfx/coords.ts';
import type { TexAtlasMgr } from '../gfx/texatlasmgr.ts';
import { ClientAnimationData } from '../gfx/animdata.ts';
import { LoadFont } from '../gfx/font.ts';
import type { Font } from '../gfx/font.ts';
import type { PackFileReader } from '../io/packfile.ts';
import {
  EventMgr,
  newEvent,
  type Event,
  gamePaintEvent,
  appStopEvent,
  gameOverEvent,
  cancelModeEvent,
  wiAutoSelectEvent,
  disableSoundEvent,
  enableSoundEvent,
  kfRedrawDirty,
} from './event.ts';
import { TimerMgr } from './timer.ts';
import { Rect } from './rect.ts';
import { kfFrmDoModal } from './uikit.ts';
import { FormMgr, MultiFormMgr, makeFormMgrHost, type ModalForm } from '../render/paintengine.ts';
import {
  MemorySoundMgr,
  defaultOptionsState,
  type ShellEnv,
  type SoundMgr,
  type OptionsState,
} from './shellform.ts';
import { kidcSetupGame, type MissionListDeps } from './missionpick.ts';
import {
  MemoryPrefStore,
  LoadPreferences,
  ApplyPreferences,
  SavePreferences,
  type PrefStore,
  type Preferences,
} from '../io/prefs.ts';
import { HtMessageBox } from './msgbox.ts';
import type { SaveStore, MissionIdentifier, PackId } from '../io/savegame.ts';
import type { Stream } from '../../../sim/src/sm/stream.ts';

// game/res.h — the startup/play form ids and controls
export const kidfPlay = 1043;
export const kidfRegisterNow = 1057;
export const kidcPlaySinglePlayer = 1001;
export const kidcPlayMultiPlayer = 1002;
export const kidcPlay = 1003;
export const kidcBuyMe = 1005;
export const kidcCredits = 1006;
export const kidcExitGame = 1007;
export const kidcLoadSavedGame = 1008;
export const kidcDownloadMissions = 1501;
export const kidcForums = 1502;
export const kidcLeaderboard = 1503;
export { kidcSetupGame };

// ht.h:2477-2480
export const kpmSavedGame = 0;
export const kpmNormal = 1;

// game/serviceurls.cpp:23-26 (the production block)
export const kszLeaderboardUrl = 'https://api.warfareincorporated.com/stats/leaderboard';
export const kszForumUrl = 'https://www.reddit.com/r/WarfareIncorporated';

// game/game.cpp:58 — int gtGameSpeed = kcmsUpdate / 10 (the boot value; the
// prefs apply + the in-game options slider move it)
export const gtGameSpeedDefault = 8;

/** The Shell's outward reaches (Game::PlayLevel/PlaySavedGame + the M5 arms). */
export interface ShellFlowDeps {
  /** ggame.PlayLevel(pmiid, NULL, nRank) — the embedder runs the mission (a
   *  GameSession via playlevel.ts) and reports the knGo* outcome. */
  playLevel(pmiid: MissionIdentifier, nRank: number, done: (nGo: number) => void): void;
  /** ggame.PlaySavedGame(pstm) — the saved-game arm (Shell.cpp:78-80). */
  playSavedGame?(pstm: Stream, done: (nGo: number) => void): void;
  /** ggame.GetLastMissionIdentifier (Shell.cpp:455). */
  getLastMissionIdentifier?(): MissionIdentifier;
  /** DrmValidate (Shell.cpp:145) — the web ships the full version: true. */
  drmValidate?(): boolean;
  /** the launch-into-reinitialize-save slot store (Shell.cpp:153). */
  saveStore?: SaveStore;
  /** PickLoadGameStream (Shell.cpp:259 — the modal picker, ui/loadsave.ts);
   *  continuation-shaped: done(pstm|null). */
  pickLoadGameStream?(done: (pstm: Stream | null) => void): void;
  /** enableNavigation (Shell.cpp:285-301 — window.onbeforeunload glue). */
  enableNavigation?(fEnable: boolean): void;
  /** M5: Lobby::Shell (Shell.cpp:394-399) — done(fStop = knShellResultAppStop). */
  playMultiplayer?(ppackid: PackId | null, done: (fStop: boolean) => void): void;
  /** M5: ShowDownloadMissionPackForm (Shell.cpp:362). */
  showDownloadMissionPackForm?(done: (packid: PackId | null) => void): void;
  /** gfDemo (Shell.cpp:208) — web ships the full version. */
  fDemo?: boolean;
}

//
// ShellSession — the shell-time session substrate (the C++ builds this ONCE in
// Game::Init, game.cpp:1276-1392, then runs gshl.Launch over it; the modal
// pump is form.cpp:578-622 dissolved exactly as GameSession.advanceTicks)
//

export interface ShellSessionOpts {
  pakr: PackFileReader;
  tam: TexAtlasMgr;
  coords: CoordMap;
  /** game.ini — the [Fonts] table (gapfnt, game.cpp:419-431). */
  gameIni: IniReader;
  /** The display size (gpdisp mode). */
  screen: { cx: number; cy: number };
  /** Renderer seam: the DibBitmap surface factory (back dib). The browser
   *  host passes the WebGL2 factory; default = the CPU reference surface. */
  createSurface?: (cx: number, cy: number) => CpuDibBitmap;
  /** The scripted host clock origin (HostGetTickCount, 1/100s ticks). */
  startTicks?: number;
  /** gsndm — defaults to the state-only MemorySoundMgr. */
  sndm?: SoundMgr;
  /** the prefs file store (HostGetPrefsFilename) — defaults in-memory. */
  prefStore?: PrefStore;
  /** the Shell flow's outward reaches. */
  flow: ShellFlowDeps;
  /** gppackm/gppim/gfDemo (missionpick.ts) — defaults: main pack only. */
  mlDeps?: Partial<MissionListDeps>;
  /** ShellEnv seam overrides (msgBox, analytics, urls, objectives, ...). */
  envExtras?: Partial<ShellEnv>;
  /** game.cpp:296-301 — the web build forces mouse mode (StylusHandler). The web
   *  boundary passes false on touch-primary devices so they default to the touch
   *  FingerHandler (the saved mouse_mode pref then decides). Default true. */
  forceMouseMode?: boolean;
}

export class ShellSession {
  readonly evm: EventMgr;
  readonly gtimm: TimerMgr;
  readonly mfrmm: MultiFormMgr;
  readonly gpfrmmSim: FormMgr;
  readonly gpfrmmInput: FormMgr;
  /** The shared back dib (gpmfrmm's dib) — frame() exposes its rgba; resize()
   *  recreates it (Display::SetSize, display.cpp:354-366). */
  back: CpuDibBitmap;
  /** Renderer seam: the surface factory (opts.createSurface; CPU default). */
  private readonly newSurface!: (cx: number, cy: number) => CpuDibBitmap;
  readonly sizPlayfield: { cx: number; cy: number };
  /** The input (console) strip height (menu button + 2*4 margin). */
  private readonly cyInput: number;
  readonly env: ShellEnv;
  readonly prefs: Preferences;
  readonly prefStore: PrefStore;
  readonly options: OptionsState;
  readonly sndm: SoundMgr;

  /** gtGameSpeed (game.cpp:58) at shell time — moved by the prefs apply and
   *  the options slider; a fresh GameSession picks it up via the embedder. */
  gtGameSpeed = gtGameSpeedDefault;

  private ticks: number;
  private readonly inputQ: Event[] = [];
  // form.cpp:578-622 — the dissolved DoModal stack (see GameSession.modalStack)
  private readonly modalStack: { pfrm: ModalForm; onLoopExit: () => void }[] = [];

  constructor(opts: ShellSessionOpts) {
    this.ticks = opts.startTicks ?? 1000;
    this.sndm = opts.sndm ?? new MemorySoundMgr();
    this.prefStore = opts.prefStore ?? new MemoryPrefStore();

    // Game::Init's prefs boot (game.cpp:286-321): load (file -> defaults) and
    // suck the option globals out of it
    this.prefs = LoadPreferences(this.prefStore)!;
    this.options = defaultOptionsState();
    this.gtGameSpeed = ApplyPreferences(
      this.prefs,
      this.options,
      this.gtGameSpeed,
      opts.forceMouseMode ?? true,
    );

    // --- the event-loop substrate (as GameSession): gtimm over the scripted
    // host clock, gevm over the host seams
    this.gtimm = new TimerMgr(() => this.ticks);
    this.evm = new EventMgr(
      {
        hostGetEvent: () => this.inputQ.shift() ?? null,
        clockMs: () => this.ticks * 10,
        cookEvent: (evt) => {
          this.mfrmm.CookEvent(evt);
        },
        checkSetRedrawDirty: () => {
          this.mfrmm.CheckSetRedrawDirty();
        },
        filterSleepTicks: (ct) => ct,
      },
      this.gtimm,
    );

    const { pakr, tam, coords } = opts;

    // gapfnt (game.cpp:419-431 — the game.ini [Fonts] table)
    const fonts: Font[] = [];
    for (let ifnt = 0; ifnt < 6; ifnt++) {
      const r = opts.gameIni.GetPropertyValue('Fonts', String(ifnt), '%s');
      if (r.count !== 1) throw new Error(`ShellSession: game.ini font ${ifnt} missing`);
      const pfnt = LoadFont(pakr, tam, r.values[0] as string);
      if (pfnt === null) throw new Error(`ShellSession: font ${r.values[0]} failed to load`);
      fonts.push(pfnt);
    }

    // --- the display partition (game.cpp:1316-1392): one back dib; the input
    // (console) strip is the MENU button height + 2*4 margin, sim strip above.
    // The shell builds it too — ggame.GetPlayfieldSize serves the ShellForm
    // zip animation even before any game runs.
    const { cx: W, cy: H } = opts.screen;
    const ptbmMenuButton = tam.CreateTBitmap('menuup.png');
    if (ptbmMenuButton === null) throw new Error('ShellSession: menuup.png missing');
    const cyInput = ptbmMenuButton.GetSize().cy + 4 * 2;
    this.cyInput = cyInput;
    const cySim = H - cyInput;
    this.sizPlayfield = { cx: W, cy: cySim };

    this.newSurface = opts.createSurface ?? ((cx, cy) => new CpuDibBitmap(cx, cy));
    this.back = this.newSurface(W, H);
    const pbmSim = this.back.Suballoc(0, cySim);
    const pbmInput = this.back.Suballoc(cySim, cyInput);

    // canvas-removal R3: the forms.ini new-by-type-name control factory
    // (Form::Init, form.cpp:63-128) + HelpControl + every misccontrols control
    // were dead (LoadForm, their only caller, became test-only in R1/R2) and are
    // deleted. The shell UI is DOM views; the only survivor of the old shell
    // helpDeps is the LoadAnimationData seam (DOM title-art / help-manual use it).
    const loadAnimationData = (pszName: string): ClientAnimationData | null => {
      // LoadAnimationData (game/Animation.cpp) over the client art stack
      const panid = new ClientAnimationData();
      if (!panid.Init(pakr, tam, pszName)) return null;
      return panid;
    };
    const host = makeFormMgrHost(coords, {
      createTBitmap: (name: string) => tam.CreateTBitmap(name),
      getFont: (ifnt: number) => fonts[ifnt],
      playSfx: (sfx) => this.sndm.PlaySfx(sfx),
      setRedrawFlags: (wf) => this.evm.SetRedrawFlags(wf),
      postEvent: (pevt, fCoalesce) => this.evm.PostEvent(pevt, fCoalesce),
      // gpdisp->FrameStart (game/sdl/display.cpp:282-289) — full repaint per
      // frame, as GameSession
      displayFrameStart: () => this.mfrmm.InvalidateRect(null),
      runModalLoop: (pfrm, onLoopExit) => {
        this.modalStack.push({ pfrm, onLoopExit });
      },
    });

    // gpmfrmm + gpfrmmSim + gpfrmmInput (game.cpp:1276-1392) — the children
    // exist (formless) so full-screen shell-form damage has updatemaps to land
    // in (MultiFormMgr.InvalidateRect distributes to children only)
    this.mfrmm = new MultiFormMgr(host);
    if (!this.mfrmm.Init(this.back, false)) throw new Error('ShellSession: mfrmm Init');
    this.gpfrmmSim = new FormMgr(host);
    if (!this.gpfrmmSim.Init(pbmSim, false)) throw new Error('ShellSession: sim FormMgr Init');
    const rc = new Rect();
    rc.Set(0, 0, W, cySim);
    this.mfrmm.AddFormMgr(this.gpfrmmSim, rc);
    this.gpfrmmInput = new FormMgr(host);
    if (!this.gpfrmmInput.Init(pbmInput, false))
      throw new Error('ShellSession: input FormMgr Init');
    rc.Set(0, cySim, W, cySim + cyInput);
    this.mfrmm.AddFormMgr(this.gpfrmmInput, rc);

    // --- the ShellEnv (the C++ globals the shell forms reach)
    this.env = {
      mfrmm: this.mfrmm,
      evm: this.evm,
      gtimm: this.gtimm,
      pakr,
      sndm: this.sndm,
      prefs: this.prefs,
      savePreferences: () => {
        SavePreferences(this.prefs, this.prefStore, this.sndm, this.options, this.gtGameSpeed);
      },
      options: this.options,
      GetPlayfieldSize: () => this.sizPlayfield,
      getSafeAreas: () => ({ left: 0, top: 0, right: 0, bottom: 0 }),
      fMultiplayer: false,
      sim: null,
      getSimUIForm: () => null,
      plrLocal: null,
      gameSpeed: () => this.gtGameSpeed,
      setGameSpeed: (t: number) => {
        this.gtGameSpeed = t;
      },
      loadAnimationData: loadAnimationData as ShellEnv['loadAnimationData'],
      // HtMessageBox (misc.cpp:471 — ui/msgbox.ts) is the default msgBox arm;
      // envExtras may override (harnesses log instead)
      msgBox: (pszTitle: string, pszText: string) => {
        HtMessageBox(this.env, 0, pszTitle, pszText);
      },
      ...opts.envExtras,
    };
  }

  /** The scripted host clock (HostGetTickCount, 1/100s ticks). */
  ticksNow(): number {
    return this.ticks;
  }

  // The display resize entry at the MENU (game/sdl/host.cpp:57-71
  // HostPollPendingResize): Display::SetSize recreates the back dib,
  // Game::OnDisplayResize re-suballocs the partitions (InitMultiFormMgr's
  // resize arm, game.cpp:1287-1390) and walks gpmfrmm->OnDisplayResize()
  // (the shell forms re-center/re-lay-out); RedrawEverything collapses to a
  // full invalidate — the next pump paints.
  resize(cx: number, cy: number): void {
    // Display::SetSize
    this.back = this.newSurface(cx, cy);

    // Game::InitMultiFormMgr (the resize arm)
    const cySim = cy - this.cyInput;
    this.sizPlayfield.cx = cx;
    this.sizPlayfield.cy = cySim;
    this.mfrmm.SetDib(this.back);
    const rc = new Rect();
    rc.Set(0, 0, cx, cySim);
    this.gpfrmmSim.SetDib(this.back.Suballoc(0, cySim));
    this.mfrmm.SetFormMgrRect(this.gpfrmmSim, rc);
    rc.Set(0, cySim, cx, cySim + this.cyInput);
    this.gpfrmmInput.SetDib(this.back.Suballoc(cySim, this.cyInput));
    this.mfrmm.SetFormMgrRect(this.gpfrmmInput, rc);

    // gpmfrmm->OnDisplayResize()
    this.mfrmm.OnDisplayResize();

    // RedrawEverything
    this.mfrmm.InvalidateRect(null);
    this.evm.SetRedrawFlags(kfRedrawDirty);
  }

  /** The shared back dib's rgba — current as of the last DrawFrame. */
  frame(): Uint8Array {
    return this.back.rgba;
  }

  /** Push a host pen event (HostGetEvent's queue). */
  injectPen(eType: number, x: number, y: number, ff = 0): void {
    const evt = newEvent(eType);
    evt.x = x;
    evt.y = y;
    evt.ff = ff; // kfEvtRightButton rides in from the host (host.cpp:358-385)
    evt.ms = this.ticks * 10;
    this.inputQ.push(evt);
  }

  /** The wiAuto synthetic control press (event.cpp:469-486). */
  pressControl(idc: number): void {
    const evt = newEvent(wiAutoSelectEvent);
    evt.x = idc;
    this.evm.PostEvent(evt, false);
  }

  /** Advance the scripted clock n ticks; after each step pump the event loop
   *  dry — the shell is ALWAYS inside some form's DoModal (form.cpp:578-622),
   *  so this pump IS that nested modal loop, with Game::FilterEvent's
   *  shell-reachable arms (game.cpp:2271-2333). Identical structure to
   *  GameSession.advanceTicks minus the in-game arms (no sim: no trigger
   *  pump, no gameOver latch). */
  advanceTicks(n: number): void {
    for (let i = 0; i < n; i++) {
      this.ticks++;
      this.DrainModalExits();
      const evt = newEvent();
      while (this.evm.GetEvent(evt, 0, this.modalStack.length !== 0)) {
        // form.cpp:595-603 — appStop/gameOver/cancelMode force-end EVERY modal
        if (
          evt.eType === appStopEvent ||
          evt.eType === gameOverEvent ||
          evt.eType === cancelModeEvent
        ) {
          while (this.modalStack.length !== 0) {
            const top = this.modalStack[this.modalStack.length - 1];
            top.pfrm.m_idcLast = top.pfrm.m_idcDefault; // form.cpp:599
            top.pfrm.EndForm(top.pfrm.m_idcDefault); // form.cpp:600-601
            this.DrainModalExits();
          }
        }
        // the shell's outermost loop is Shell::Launch's IsAppStopping checks —
        // an appStop stops the pump for this embedder slice
        if (evt.eType === appStopEvent) return;
        this.DrainModalExits();
        if (this.FilterEvent(evt)) continue;
        const top = this.modalStack[this.modalStack.length - 1];
        if (top !== undefined && top.pfrm.OnFilterEvent(evt)) continue;
        this.DispatchEvent(evt);
        this.DrainModalExits();
      }
    }
  }

  // game/event.cpp:467-494 — gevm.DispatchEvent (no in-game input overlay in
  // the shell: ggame.GetSimUIForm() == NULL, so wiAuto routes to the modal
  // form only)
  private DispatchEvent(pevt: Event): boolean {
    if (pevt.eType === wiAutoSelectEvent) {
      // GetModalForm() is always null now (no forms.ini Form is ever modal), so
      // this synthetic-control arm never fires; kept for shape (a modal layer, if
      // one ever set kfFrmDoModal, would satisfy ModalForm).
      const pfrm = this.mfrmm.GetModalForm() as unknown as ModalForm | null;
      if (pfrm !== null) {
        pfrm.WiAutoSetLastControl(pevt.x & 0xffff);
        pfrm.OnControlSelected(pevt.x & 0xffff);
      }
      return true;
    }
    const pfrm = this.mfrmm.GetFormPtr(pevt.idf);
    if (pfrm !== null) return pfrm.EventProc(pevt);
    return false;
  }

  // form.cpp:578 + 624-632 — see GameSession.DrainModalExits
  private DrainModalExits(): void {
    while (this.modalStack.length !== 0) {
      const top = this.modalStack[this.modalStack.length - 1];
      if (top.pfrm.GetFlags() & kfFrmDoModal) break;
      this.modalStack.pop();
      top.onLoopExit();
    }
  }

  // Game::FilterEvent (game.cpp:2271-2333) — the arms the SHELL reaches:
  // UpdateTriggers is a no-op without a Simulation; checkGameOver/transport/
  // mpEndMission arms are MP; gameSuspendEvent is the OS-alert platform arm.
  private FilterEvent(evt: Event): boolean {
    switch (evt.eType) {
      case gamePaintEvent:
        // game.cpp:2287-2294 — gcPaint++ (FPS stat; no SimUIForm here), draw
        this.mfrmm.DrawFrame(true);
        return true;

      case disableSoundEvent:
        this.sndm.SaveStateAndClear(); // game.cpp:2322-2324
        break;

      case enableSoundEvent:
        this.sndm.RestoreState(); // game.cpp:2326-2328
        break;
    }
    return false;
  }
}
